import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { EyeOff, Eye } from "lucide-react";
import { api, fmtError } from "@/lib/api";
import { fmtDate } from "@/constants/labels";
import RatingStars from "@/components/RatingStars";

/**
 * Onglet admin de modération des avis.
 * - Sous-filtres : Visibles / Masqués / Tous
 * - Bouton Masquer (motif obligatoire, min 5c.) / Restaurer par ligne
 * - Affiche noms + rôles émetteur/destinataire, note, commentaire, réponse
 * - Un avis masqué n'entre plus dans la note moyenne (backend applique
 *   `hidden!=true` dans les agrégations sur `note_moyenne`)
 */
const FILTERS = [
  { id: "visible", label: "Visibles" },
  { id: "hidden", label: "Masqués" },
  { id: "all", label: "Tous" },
];

export default function AdminAvis() {
  const [filter, setFilter] = useState("visible");
  const [rows, setRows] = useState([]);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      const params = {};
      if (filter === "visible") params.hidden = false;
      if (filter === "hidden") params.hidden = true;
      const { data } = await api.get("/admin/avis", { params });
      setRows(data);
    } catch (err) { toast.error(fmtError(err)); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [filter]);

  const hide = async (a) => {
    const motif = window.prompt(
      `Masquer cet avis ?\n\nNote: ${a.note}/5\nCommentaire: « ${a.commentaire.slice(0, 100)}${a.commentaire.length > 100 ? "…" : ""} »\n\nMotif de la modération (min 5 caractères) :`,
    );
    if (!motif || motif.trim().length < 5) {
      if (motif !== null) toast.error("Motif trop court (min 5 caractères)");
      return;
    }
    setBusy(true);
    try {
      await api.post(`/admin/avis/${a.id}/hide`, { motif: motif.trim() });
      toast.success("Avis masqué. Il ne compte plus dans la note moyenne.");
      load();
    } catch (err) { toast.error(fmtError(err)); }
    finally { setBusy(false); }
  };

  const unhide = async (a) => {
    if (!window.confirm("Restaurer cet avis ? Il redeviendra visible et comptera dans la note moyenne.")) return;
    setBusy(true);
    try {
      await api.post(`/admin/avis/${a.id}/unhide`);
      toast.success("Avis restauré.");
      load();
    } catch (err) { toast.error(fmtError(err)); }
    finally { setBusy(false); }
  };

  const Tab = ({ id, label, count }) => (
    <button
      onClick={() => setFilter(id)}
      data-testid={`admin-avis-filter-${id}`}
      className={`px-3 py-1.5 text-xs font-semibold rounded-full border ${
        filter === id ? "bg-[#1B3A5C] text-white border-[#1B3A5C]" : "bg-white text-[#3E4C59] border-[#CBD5E1]"
      }`}
    >{label}{count !== undefined && <span className="ml-1 opacity-70">({count})</span>}</button>
  );

  return (
    <div className="mt-4">
      <div className="flex flex-wrap items-center gap-2 mb-3">
        {FILTERS.map((f) => <Tab key={f.id} id={f.id} label={f.label} />)}
        <span className="ml-auto text-xs text-[#3E4C59]">{rows.length} avis</span>
      </div>

      <div className="card overflow-x-auto">
        <table className="table-compact w-full">
          <thead>
            <tr>
              <th>Date</th>
              <th>Émetteur</th>
              <th>Destinataire</th>
              <th>Note</th>
              <th>Commentaire</th>
              <th>Réponse</th>
              <th>Statut</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 && <tr><td colSpan={8} className="text-center text-[#3E4C59]">Aucun avis</td></tr>}
            {rows.map((a) => (
              <tr key={a.id} data-testid={`admin-avis-row-${a.id}`} className={a.hidden ? "opacity-50" : ""}>
                <td className="whitespace-nowrap">{fmtDate(a.created_at)}</td>
                <td>
                  <div className="text-sm">{a.from?.nom || "—"}</div>
                  <div className="text-xs text-[#3E4C59]">{a.from?.role}</div>
                </td>
                <td>
                  <div className="text-sm">{a.to?.nom || "—"}</div>
                  <div className="text-xs text-[#3E4C59]">{a.to?.role}</div>
                </td>
                <td><RatingStars value={a.note} readOnly size={14} /></td>
                <td className="max-w-[280px]">
                  <div className="text-sm text-[#1F2933] whitespace-pre-line">{a.commentaire}</div>
                </td>
                <td className="max-w-[240px] text-xs text-[#3E4C59]">
                  {a.reponse ? <span className="whitespace-pre-line">{a.reponse}</span> : <em>—</em>}
                </td>
                <td>
                  {a.hidden ? (
                    <div>
                      <span className="badge-danger">Masqué</span>
                      <div className="text-xs text-[#C0392B] mt-1 max-w-[180px]">
                        {a.hidden_motif}
                      </div>
                    </div>
                  ) : <span className="badge-valid">Visible</span>}
                </td>
                <td>
                  {a.hidden ? (
                    <button
                      onClick={() => unhide(a)}
                      disabled={busy}
                      data-testid={`admin-avis-unhide-${a.id}`}
                      className="btn-outline text-xs flex items-center gap-1"
                    ><Eye size={12} /> Restaurer</button>
                  ) : (
                    <button
                      onClick={() => hide(a)}
                      disabled={busy}
                      data-testid={`admin-avis-hide-${a.id}`}
                      className="btn-danger text-xs flex items-center gap-1"
                    ><EyeOff size={12} /> Masquer</button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
